import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle, Users, UserX, AlertTriangle, Download, Eye, EyeOff } from 'lucide-react';
import EmailTemplateFormFields from './EmailTemplateFormFields';

const ScrapingPreviewModal = ({
  isOpen,
  onClose,
  previewData,
  onConfirm,
  isConfirming = false
}) => {
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);

  // Initialize selected leads (all valid leads selected by default)
  React.useEffect(() => {
    if (previewData?.previewLeads) {
      setSelectedLeads(previewData.previewLeads.map((_, index) => index));
    }
  }, [previewData?.previewLeads]);

  // Load email templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadEmailTemplates();
    }
  }, [isOpen]);

  const loadEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await fetch('/api/email-templates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmailTemplates(data.templates || []);

        // Auto-select the first template if available
        if (data.templates && data.templates.length > 0) {
          setSelectedTemplateId(data.templates[0].id);
          // Load template details for preview
          loadTemplateDetails(data.templates[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load email templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadTemplateDetails = async (templateId) => {
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }

    setLoadingTemplateDetails(true);
    try {
      const response = await fetch(`/api/email-templates/${templateId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedTemplate(data.template || null);
      }
    } catch (error) {
      console.error('Failed to load template details:', error);
      setSelectedTemplate(null);
    } finally {
      setLoadingTemplateDetails(false);
    }
  };

  if (!isOpen || !previewData) return null;

  const { previewLeads, duplicateLeads, invalidLeads, summary } = previewData;

  // Custom scrollbar styles for WebKit browsers
  const scrollbarStyles = `
    .template-preview-scroll::-webkit-scrollbar,
    .modal-content-scroll::-webkit-scrollbar {
      width: 8px;
    }
    .template-preview-scroll::-webkit-scrollbar-track,
    .modal-content-scroll::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 4px;
    }
    .template-preview-scroll::-webkit-scrollbar-thumb,
    .modal-content-scroll::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }
    .template-preview-scroll::-webkit-scrollbar-thumb:hover,
    .modal-content-scroll::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
  `;

  const handleSelectAll = () => {
    if (selectedLeads.length === previewLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(previewLeads.map((_, index) => index));
    }
  };

  const handleSelectLead = (index) => {
    setSelectedLeads(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleConfirm = () => {
    const confirmedLeads = selectedLeads.map(index => previewLeads[index]);
    onConfirm(confirmedLeads, selectedTemplateId);
  };

  const downloadScrapingResults = () => {
    // Prepare leads data for download (excluding invalid leads)
    const allLeads = [];

    // Add valid leads with status
    if (previewLeads && previewLeads.length > 0) {
      previewLeads.forEach(lead => {
        allLeads.push({
          ...lead,
          status: 'Valid'
        });
      });
    }

    // Add duplicate leads with status
    if (duplicateLeads && duplicateLeads.length > 0) {
      duplicateLeads.forEach(lead => {
        allLeads.push({
          ...lead,
          status: `Duplicate - ${lead.reason || 'Already exists'}`
        });
      });
    }

    // Note: Invalid leads are excluded from download

    if (allLeads.length === 0) {
      alert('No data to download');
      return;
    }

    // Create CSV content
    const headers = ['First Name', 'Last Name', 'Email', 'Company', 'Job Title', 'Location', 'Country', 'Status'];
    const csvContent = [
      headers.join(','),
      ...allLeads.map(lead => [
        `"${(lead.first_name || '').replace(/"/g, '""')}"`,
        `"${(lead.last_name || '').replace(/"/g, '""')}"`,
        `"${(lead.email || '').replace(/"/g, '""')}"`,
        `"${(lead.company || '').replace(/"/g, '""')}"`,
        `"${(lead.job_title || '').replace(/"/g, '""')}"`,
        `"${(lead.location || '').replace(/"/g, '""')}"`,
        `"${(lead.country || '').replace(/"/g, '""')}"`,
        `"${(lead.status || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `scraping-results-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <style>{scrollbarStyles}</style>
      <div className="bg-white rounded-lg shadow-xl max-w-full w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Review Scraped Leads
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Review and confirm the leads before saving to your database
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isConfirming}
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto modal-content-scroll" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 #f1f5f9'
        }}>
          {/* Summary Stats */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 bg-green-50 p-3 rounded-lg">
              <Users className="text-green-600" size={20} />
              <div>
                <div className="text-lg font-semibold text-green-900">
                  {summary?.validNewLeads || 0}
                </div>
                <div className="text-sm text-green-600">Valid New Leads</div>
              </div>
            </div>
            <div className="flex items-center space-x-3 bg-yellow-50 p-3 rounded-lg">
              <UserX className="text-yellow-600" size={20} />
              <div>
                <div className="text-lg font-semibold text-yellow-900">
                  {summary?.duplicates || 0}
                </div>
                <div className="text-sm text-yellow-600">Duplicates</div>
              </div>
            </div>
            <div className="flex items-center space-x-3 bg-red-50 p-3 rounded-lg">
              <AlertTriangle className="text-red-600" size={20} />
              <div>
                <div className="text-lg font-semibold text-red-900">
                  {summary?.invalid || 0}
                </div>
                <div className="text-sm text-red-600">Invalid</div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Template Selection */}
        <div className="p-6 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Template
              </label>
              <select
                value={selectedTemplateId || ''}
                onChange={(e) => {
                  const templateId = e.target.value;
                  setSelectedTemplateId(templateId);
                  loadTemplateDetails(templateId);
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                disabled={loadingTemplates || isConfirming}
              >
                {loadingTemplates ? (
                  <option value="">Loading templates...</option>
                ) : emailTemplates.length === 0 ? (
                  <option value="">No templates available</option>
                ) : (
                  <>
                    <option value="">Select a template</option>
                    {emailTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.type})
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <button
                onClick={() => setShowTemplatePreview(!showTemplatePreview)}
                disabled={!selectedTemplateId || loadingTemplateDetails}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {showTemplatePreview ? <EyeOff size={16} /> : <Eye size={16} />}
                <span>{showTemplatePreview ? 'Hide Preview' : 'Preview Template'}</span>
              </button>
              <div className="text-sm text-gray-600 text-right">
                <p>Selected template will be used for these leads</p>
              </div>
            </div>
          </div>
        </div>

        {/* Template Preview Section */}
        {showTemplatePreview && selectedTemplate && (
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Template Preview: {selectedTemplate.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Full template content displayed below
                  </p>
                </div>
                {loadingTemplateDetails && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Loading template...</span>
                  </div>
                )}
              </div>

              {selectedTemplate && !loadingTemplateDetails && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <EmailTemplateFormFields
                    formData={selectedTemplate}
                    onInputChange={() => {}} // Read-only, so no-op
                    showTemplateHeader={false}
                    showTemplateType={false}
                    templateReadOnly={true}
                    disabled={true}
                    compact={false}
                    linkPreviews={(() => {
                      try {
                        return selectedTemplate.link_previews ? JSON.parse(selectedTemplate.link_previews) : {};
                      } catch (e) {
                        console.warn('Failed to parse link_previews:', e);
                        return {};
                      }
                    })()}
                    attachments={(() => {
                      try {
                        return selectedTemplate.attachments ? JSON.parse(selectedTemplate.attachments) : [];
                      } catch (e) {
                        console.warn('Failed to parse attachments:', e);
                        return [];
                      }
                    })()}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Leads List */}
        <div className="p-6">
          {previewLeads && previewLeads.length > 0 ? (
            <>
              {/* Select All */}
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === previewLeads.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({selectedLeads.length} of {previewLeads.length} selected)
                  </span>
                </label>
              </div>

              {/* Leads Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Select
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {previewLeads.map((lead, index) => (
                      <tr key={index} className={selectedLeads.includes(index) ? 'bg-blue-50' : ''}>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(index)}
                            onChange={() => handleSelectLead(index)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {lead.first_name} {lead.last_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{lead.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{lead.company}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{lead.job_title}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {lead.location} {lead.country && `, ${lead.country}`}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No valid leads found</h3>
              <p className="mt-1 text-sm text-gray-500">
                All scraped leads were either duplicates or invalid.
              </p>
            </div>
          )}

          {/* Show duplicates/invalid if any */}
          {(duplicateLeads?.length > 0 || invalidLeads?.length > 0) && (
            <div className="mt-6 space-y-4">
              {duplicateLeads?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">
                    Duplicate Leads ({duplicateLeads.length})
                  </h4>
                  <div className="bg-yellow-50 rounded-md p-3 max-h-32 overflow-y-auto">
                    {duplicateLeads.slice(0, 5).map((lead, index) => (
                      <div key={index} className="text-sm text-yellow-700">
                        {lead.first_name} {lead.last_name} ({lead.email}) - {lead.reason}
                      </div>
                    ))}
                    {duplicateLeads.length > 5 && (
                      <div className="text-sm text-yellow-600 mt-1">
                        ... and {duplicateLeads.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {invalidLeads?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-red-800 mb-2">
                    Invalid Leads ({invalidLeads.length})
                  </h4>
                  <div className="bg-red-50 rounded-md p-3 max-h-32 overflow-y-auto">
                    {invalidLeads.slice(0, 5).map((lead, index) => (
                      <div key={index} className="text-sm text-red-700">
                        {lead.first_name} {lead.last_name} ({lead.email}) - {lead.reason}
                      </div>
                    ))}
                    {invalidLeads.length > 5 && (
                      <div className="text-sm text-red-600 mt-1">
                        ... and {invalidLeads.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
        {/* End Scrollable Content Area */}

        {/* Footer - Fixed */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-600">
            {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} selected to save
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isConfirming}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={downloadScrapingResults}
              disabled={isConfirming}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center space-x-2"
            >
              <Download size={16} />
              <span>Download CSV</span>
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedLeads.length === 0 || !selectedTemplateId || isConfirming}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isConfirming && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <Check size={16} />
              <span>
                {isConfirming ? 'Saving...' : `Confirm & Save ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrapingPreviewModal;